import { ReactNode, FC, useState } from 'react';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import { TextFieldProps } from '@mui/material/TextField';
import { Visibility, VisibilityOff } from '@mui/icons-material';

interface BaseTextFieldProps extends Omit<TextFieldProps, 'InputProps'> {
    label?: string;
    startIcon?: ReactNode;
    endIcon?: ReactNode; // if provided, skip built-in eye
    showDefaultPasswordToggle?: boolean; // default true
    inputRef?: React.Ref<HTMLInputElement>;
}

const BaseTextField: FC<BaseTextFieldProps> = ({
    label,
    startIcon,
    endIcon,
    value,
    type,
    showDefaultPasswordToggle = true,
    inputRef,
    ...props
}) => {
    const isPassword = type === 'password';
    const usingCustomEndIcon = Boolean(endIcon);
    const shouldUseInternalToggle = isPassword && showDefaultPasswordToggle && !usingCustomEndIcon;

    const [showPassword, setShowPassword] = useState(false);
    const effectiveType = shouldUseInternalToggle && showPassword ? 'text' : type;

    const resolvedEndAdornment = usingCustomEndIcon ? (
        endIcon // assume caller already wrapped with <InputAdornment position="end">
    ) : shouldUseInternalToggle ? (
        <InputAdornment position="end">
            <IconButton
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onClick={(e) => {
                    e.preventDefault();
                    setShowPassword((p) => !p);
                }}
                onMouseDown={(e) => e.preventDefault()}
                edge="end"
            >
                {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
        </InputAdornment>
    ) : undefined;

    return (
        <TextField
            label={label}
            variant="outlined"
            type={effectiveType}
            inputRef={inputRef} // attaches to the native <input/>
            InputProps={{
                startAdornment: startIcon ? <InputAdornment position="start">{startIcon}</InputAdornment> : undefined,
                endAdornment: resolvedEndAdornment,
            }}
            value={value}
            {...props}
        />
    );
};

export default BaseTextField;
